import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import { AbstractStorageManager } from './abstract-storage-manager.ts'
import { SessionStorageKey, SettingsStorageKey } from '@core/storage/keys.ts'
import { DataStoreName, StorageType } from '@core/storage/abstract-data-store.ts'

/* @internal */
export type SessionStore = {
    _setSessionStart(start: Date): Promise<Date | null>
    _getSessionStart(): Promise<Date | null>
    _getSessionRuntimeSeconds(): Promise<number>
    _setSessionRuntimeSeconds(runtimeSeconds: number): Promise<number | null>
    _getSessionPageViewCount(): Promise<number>
    _getCurrentPageViewStart(): Promise<Date | null>
    _incrementSessionPageViewCount(): Promise<void>
    _setHeartbeatSessionDate(start: Date): Promise<boolean>
    _getHeartbeatSessionDate(): Promise<Date | null>
    _setLastActivity(start: Date): Promise<boolean>
    _getLastActivity(): Promise<Date | null>
    _setSessionId(id: string): Promise<string | null>
    _getSessionId(): Promise<string | null>
    _purgeRetiredKeys(): Promise<void>
}

export class SessionStorageManager extends AbstractStorageManager implements SessionStore {
    public constructor(logger?: Logger) {
        super(
            DataStoreName.sdk,
            [StorageType.SESSION, StorageType.MEM],
            logger ?? TaggedLogger._getInstance('SessionStore'),
        )
    }

    public async _setSessionStart(start: Date): Promise<Date | null> {
        const req = await this._setData<Date>(SessionStorageKey.SESSION_START, start)
        return req.unwrapOrNull()
    }

    public async _getSessionStart(): Promise<Date | null> {
        const req = await this._getData<number>(SessionStorageKey.SESSION_START)
        const cachedTime = req.unwrapOrNull()

        return cachedTime ? new Date(cachedTime) : null
    }

    public async _getCurrentPageViewStart(): Promise<Date | null> {
        const req = await this._getData<number>(SessionStorageKey.SESSION_PAGE_VIEW_START)
        const cachedTime = req.unwrapOrNull()

        return cachedTime ? new Date(cachedTime) : null
    }

    public async _getSessionPageViewCount(): Promise<number> {
        const count = await this._getData<number>(SessionStorageKey.SESSION_PAGE_VIEWS)
        return count.unwrapOr(0)
    }

    public async _getSessionRuntimeSeconds(): Promise<number> {
        const runtimeSeconds = await this._getData<number>(SessionStorageKey.SESSION_RUNTIME)
        return runtimeSeconds.unwrapOr(0)
    }

    public async _setSessionRuntimeSeconds(runtimeSeconds: number): Promise<number | null> {
        const req = await this._setData(SessionStorageKey.SESSION_RUNTIME, runtimeSeconds)
        return req.unwrapOrNull()
    }

    public async _incrementSessionPageViewCount(): Promise<void> {
        const count = await this._getSessionPageViewCount()

        await Promise.all([
            this._setData(SessionStorageKey.SESSION_PAGE_VIEWS, count + 1),
            this._setData(SessionStorageKey.SESSION_PAGE_VIEW_START, new Date().getTime()),
        ])
    }

    public async _setLastActivity(start: Date): Promise<boolean> {
        const req = await this._setData<number>(SessionStorageKey.SESSION_LAST_ACTIVITY, start.getTime())
        return req.isOk()
    }

    public async _getLastActivity(): Promise<Date | null> {
        const req = await this._getData<number>(SessionStorageKey.SESSION_LAST_ACTIVITY)
        const cachedTime = req.unwrapOrNull()

        return cachedTime ? new Date(cachedTime) : null
    }

    public async _setSessionId(id: string): Promise<string | null> {
        const req = await this._setData<string>(SessionStorageKey.SESSION_ID, id)
        return req.unwrapOrNull()
    }

    public async _getSessionId(): Promise<string | null> {
        const req = await this._getData<string>(SessionStorageKey.SESSION_ID)
        return req.unwrapOrNull()
    }

    public async _setHeartbeatSessionDate(start: Date): Promise<boolean> {
        const req = await this._setData<number>(SessionStorageKey.SESSION_HEARTBEAT_SESSION, start.getTime())
        return req.isOk()
    }

    public async _getHeartbeatSessionDate(): Promise<Date | null> {
        const req = await this._getData<number>(SessionStorageKey.SESSION_HEARTBEAT_SESSION)
        const cachedTime = req.unwrapOrNull()

        return cachedTime ? new Date(cachedTime) : null
    }

    /**
     * Drops keys this manager's blob no longer has a reader for.
     *
     * The prompts cache moved into the merged settings document, which lives under its own
     * sessionStorage key. Nothing writes the old key any more, but a tab that navigates from a
     * page running an older self-hosted bundle to one running this version keeps whatever that
     * page wrote for the rest of the tab's life - and this blob is re-parsed and re-serialised
     * by the session timer every second, so a stale prompts payload is paid for repeatedly.
     */
    public async _purgeRetiredKeys(): Promise<void> {
        await this._removeData(SettingsStorageKey.PROMPTS_CACHE)
    }
}
